import { MetricValueMode } from '@statsbomb/parachute-types';
import { atom, Atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { CLIENT_ONLY_SORT_KEYS } from '@/consts/ordering';
import { Nullable } from '@/types/generic';
import { MetricLookupMap } from '@/types/metric';
import { SORT_DIRECTION } from '@/types/ordering';
import { TableColumnsEditorTabs, TableId } from '@/types/table';
import { loadable } from '@/utils/jotai';
import { getDataKeyFromColumnKey } from '@/utils/metrics';
import { unwrappedMetricLookupMapAtom } from './queries/metric';
import { tableColumnsAtom } from './queries/tableColumns';
import { customTableColumnsAtom } from './queries/userConfigs';

/**
 * Sorting in the application is handled both server side and client side under different circumstances.
 * In order to prevent API calls from triggering when we just want to sort client side, we have separate
 * atoms for server side sorting and client side sorting.
 *
 * When the conditions are met for client side sorting, we just update the clientOrder atoms. This
 * will allow the code which needs to perform the client sorting to do so without triggering an API call.
 *
 * If we need to sort server side, we should call the setter on orderByAtom and orderDirectionAtom. This will
 * BOTH the server side and client side atoms. The getter of these atoms will return the client side values.
 *
 * Doing this means that places which need to show the currently applied sort order will work regardless of whether
 * we're doing server side or client side sorting.
 */
export const serverOrderByAtom = atom<Nullable<string>>(null);
export const serverOrderDirectionAtom = atom<SORT_DIRECTION>(SORT_DIRECTION.DESC);

export const clientOrderByAtom = atom<Nullable<string>>(null);
export const clientOrderDirectionAtom = atom<SORT_DIRECTION>(SORT_DIRECTION.DESC);

export const metricValueModeAtom = atom<MetricValueMode>(MetricValueMode.Per90);

export const showRankAtom = atom<boolean>(true);

export const orderByAtom = atom(
  get => get(clientOrderByAtom),
  (_, set, newValue: string) => {
    set(clientOrderByAtom, newValue);
    // Client-only keys have no server-side column equivalent — promoting them would 500 the API.
    if (CLIENT_ONLY_SORT_KEYS.has(newValue)) {
      set(serverOrderByAtom, null);
    } else {
      set(serverOrderByAtom, newValue);
    }
  },
);

export const defaultTableColumnsAtom = atom<string[]>([]);
export const visibleTableColumnsAtom = atom<string[]>([]);

export const unwrappedVisibleTableColumnsAtom = unwrap(visibleTableColumnsAtom, prev => prev || []);

export const getOrderByMetricKey = (
  orderBy: Nullable<string>,
  visibleColumns: string[],
  metricLookupMap: MetricLookupMap | undefined,
  metricValueMode: MetricValueMode,
): Nullable<string> => {
  if (!orderBy) return null;

  if (!visibleColumns.includes(orderBy)) {
    return orderBy;
  }

  return getDataKeyFromColumnKey(orderBy, metricLookupMap, metricValueMode);
};

const createOrderByMetricKeyAtom = (sourceOrderByAtom: Atom<Nullable<string>>) =>
  atom(get =>
    getOrderByMetricKey(
      get(sourceOrderByAtom),
      get(unwrappedVisibleTableColumnsAtom),
      get(unwrappedMetricLookupMapAtom),
      get(metricValueModeAtom),
    ),
  );
export const serverOrderByMetricKeyAtom = createOrderByMetricKeyAtom(serverOrderByAtom);
export const clientOrderByMetricKeyAtom = createOrderByMetricKeyAtom(clientOrderByAtom);

export const orderDirectionAtom = atom(
  get => get(clientOrderDirectionAtom),
  (_, set, newValue: SORT_DIRECTION) => {
    set(serverOrderDirectionAtom, newValue);
    set(clientOrderDirectionAtom, newValue);
  },
);

// one write, so the server sort never briefly pairs the new column with the old direction
export const sortAtom = atom(null, (_, set, { orderBy, direction }: { orderBy: string; direction: SORT_DIRECTION }) => {
  set(orderDirectionAtom, direction);
  set(orderByAtom, orderBy);
});

export const tableColumnsEditorActiveTabAtom = atom(TableColumnsEditorTabs.COLUMN_SELECT);

export const tableIdAtom = atom<Nullable<TableId>>(null);

const defaultTableConfig = { configId: '', selectedColumns: [] };

export const tableConfigAtom = atom(async get => {
  const tableId = get(tableIdAtom);
  const customTableColumns = await get(customTableColumnsAtom);
  const tableConfig = customTableColumns.find(tableConfig => tableConfig.definition.tableId === tableId);

  if (!tableConfig) {
    return defaultTableConfig;
  }

  const availableTableColumns = (await get(tableColumnsAtom)).map(col => col.tableColumn.key);

  const selectedColumns = tableConfig.definition.selectedColumns.filter(column =>
    availableTableColumns.includes(column),
  );

  return {
    configId: tableConfig.configId,
    selectedColumns,
  };
});

export const loadableTableConfigAtom = loadable(tableConfigAtom);

export const tableSelectedRowIdsAtom = atom<string[]>([]);
